/**
 * Speeds (Foundry: CreaturePF2e#prepareMovementData and system/statistic/speed.ts). For each
 * movement type the candidates are the BaseSpeed synthetics whose predicates pass plus the
 * ancestry's base (land); forced candidates win, else the highest. A speed whose formula reads
 * another speed's total is derived from it: equal (fly = land) shares the parent's base and
 * modifiers, scaled (land / 2) starts from the parent's total and takes only its own type's
 * modifiers, independent speeds take the all-speeds, speed and type-speed domains. Then the
 * FlatModifiers stack on top, to a minimum of 5 feet when the base is above 0.
 */
import type { SpeedData } from "./actor-data";
import { cloneModifier, resolveModifiers, signed, type Modifier } from "./modifiers";
import type { EngineState } from "./state";
import { extractModifiers, type BaseSpeedSynthetic } from "./synthetics";
import { MOVEMENT_TYPES, MOVEMENT_TYPE_LABELS, type MovementType } from "./values";

export interface DerivedSpeed {
  type: MovementType;
  label: string;
  base: number;
  source: string | null;
  /** The base as shown: for a speed equal to another, the parent's base plus the modifiers they share. */
  displayBase: number;
  value: number;
  modifiers: Modifier[];
  breakdown: string;
  /** The speed this one derives from, when its BaseSpeed formula reads another speed's total. */
  derivedFrom: MovementType | null;
  domains: string[];
}

interface Candidate {
  value: number;
  source: string | null;
  force: boolean;
  formula: string | number;
  dependsOn: MovementType[];
}

type DeriveKind = "equal" | "scaled" | "independent";

const SPEED_VALUE_PATTERN = /movement\.speeds\.(land|burrow|climb|fly|swim)\.value/;

function deriveParentType(formula: string | number, dependsOn: MovementType[]): MovementType | null {
  if (dependsOn.length === 0) return null;
  if (typeof formula === "string") {
    const fromValue = dependsOn.find((type) => formula.includes(`speeds.${type}.value`));
    if (fromValue) return fromValue;
  }
  return dependsOn[0] ?? null;
}

function classifyDeriveKind(formula: string | number, resolved: number, parentValue: number | undefined): DeriveKind {
  if (typeof formula !== "string" || parentValue === undefined || !SPEED_VALUE_PATTERN.test(formula)) return "independent";
  return resolved === parentValue ? "equal" : "scaled";
}

/** Foundry's English formats (`PF2E.Actor.Speed.*`), overridden by the Snapshot's strings when present. */
const FORMATS = { BaseLabel: "{value}-Foot {type} Speed", BaseWithSource: "{value}-Foot {type} Speed ({source})", TypeLabel: "{type} Speed" };

function format(state: EngineState, key: keyof typeof FORMATS, values: Record<string, string>): string {
  const fullKey = key === "TypeLabel" ? "PF2E.Actor.Speed.Type.Label" : `PF2E.Actor.Speed.${key}`;
  const template = state.catalogue?.localize(fullKey) ?? fullKey;
  return (template === fullKey ? FORMATS[key] : template).replace(/\{(\w+)\}/g, (whole, name: string) => values[name] ?? whole);
}

function typeLabel(state: EngineState, type: MovementType): string {
  const key = `PF2E.Actor.Speed.Type.${type.charAt(0).toUpperCase()}${type.slice(1)}`;
  const text = state.catalogue?.localize(key) ?? key;
  return text === key ? MOVEMENT_TYPE_LABELS[type] : text;
}

interface SpeedStatistic {
  type: MovementType;
  base: number;
  source: string | null;
  domains: string[];
  modifiers: Modifier[];
  value: number;
  parent: SpeedStatistic | null;
}

/** Foundry's SpeedStatistic: extract for the domains, add the given modifiers, stack. */
function buildStatistic(state: EngineState, type: MovementType, base: number, source: string | null, domains: string[], additional: Modifier[], parent: SpeedStatistic | null): SpeedStatistic {
  const unique = [...new Set(domains)];
  const options = state.rollOptions.get(unique);
  const synthetic = extractModifiers(state.synthetics, unique, { test: options });
  const { modifiers, total } = resolveModifiers([...synthetic, ...additional.map((m) => cloneModifier(m, { domains: [...unique] }))], options);
  const safeBase = Math.max(0, Math.trunc(base));
  const value = safeBase > 0 ? Math.max(5, safeBase + total) : Math.max(0, safeBase + total);
  return { type, base: safeBase, source, domains: unique, modifiers, value, parent };
}

function breakdownOf(state: EngineState, statistic: SpeedStatistic): { breakdown: string; displayBase: number } {
  const type = typeLabel(state, statistic.type);
  const parent = statistic.parent;
  const enabled = statistic.modifiers.filter((m) => m.enabled && m.value !== 0);
  let shared: Modifier[] = [];
  let own = enabled;
  if (parent) {
    const parentSlugs = new Set(parent.modifiers.map((m) => m.slug));
    shared = enabled.filter((m) => parentSlugs.has(m.slug));
    own = enabled.filter((m) => !parentSlugs.has(m.slug));
  }
  const displayBase = statistic.base + shared.reduce((sum, m) => sum + m.value, 0);
  const baseLabel = statistic.source
    ? format(state, "BaseWithSource", { value: String(displayBase), type, source: statistic.source })
    : format(state, "BaseLabel", { value: String(displayBase), type });
  return { breakdown: [baseLabel, ...own.map((m) => `${m.label} ${signed(m.value)}`)].join(", "), displayBase };
}

/**
 * Prepare every movement type. `additional` are modifiers the actor adds itself (armour and
 * shield speed penalties), with domains of their own.
 */
export function prepareMovement(state: EngineState, additional: Modifier[] = []): Partial<Record<MovementType, DerivedSpeed>> & { land: DerivedSpeed } {
  const { actor, synthetics } = state;
  const speeds = actor.system.movement.speeds;
  const baseOptions = state.rollOptions.get();
  const statistics: Partial<Record<MovementType, SpeedStatistic>> = {};

  // Ancestry (and ActiveEffectLike) bases before the traces overwrite them.
  const systemValues: Partial<Record<MovementType, { value: number; source: string | null } | null>> = {};
  for (const type of MOVEMENT_TYPES) {
    const data = speeds[type];
    if (type === "land") systemValues[type] = { value: Math.max(0, Math.trunc(Number(data?.base ?? 0) || 0)), source: data?.source ?? null };
    else systemValues[type] = data && Number(data.value) > 0 ? { value: Math.trunc(Number(data.value)), source: data.source ?? null } : null;
  }

  const selectCandidate = (candidates: Candidate[]): Candidate | null => {
    if (candidates.length === 0) return null;
    const forced = candidates.filter((c) => c.force);
    const pool = forced.length > 0 ? forced : candidates;
    return pool.reduce((best, c) => (c.value > best.value ? c : best));
  };

  const extrasFor = (type: MovementType, kind: DeriveKind): Modifier[] => {
    if (kind === "scaled") return additional.filter((m) => m.domains.includes(`${type}-speed`)).map((m) => cloneModifier(m));
    if (kind === "equal" || type === "land") return additional.map((m) => cloneModifier(m));
    return additional.filter((m) => ["all-speeds", `${type}-speed`].some((d) => m.domains.includes(d))).map((m) => cloneModifier(m));
  };

  const build = (type: MovementType, selected: Candidate): SpeedStatistic => {
    const parentType = deriveParentType(selected.formula, selected.dependsOn);
    const parent = parentType ? statistics[parentType] : undefined;
    const kind: DeriveKind = parent ? classifyDeriveKind(selected.formula, selected.value, parent.value) : "independent";
    const extras = extrasFor(type, kind);
    if (kind === "equal" && parent) {
      state.rollOptions.set(`${type}-speed`, `derived-from:${parent.type}`);
      return buildStatistic(state, type, parent.base, selected.source ?? parent.source, [...parent.domains, `${type}-speed`], extras, parent);
    }
    if (kind === "scaled" && parent) {
      return buildStatistic(state, type, selected.value, selected.source ?? parent.source, [`${type}-speed`], extras.filter((m) => m.domains.includes(`${type}-speed`)), null);
    }
    return buildStatistic(state, type, selected.value, selected.source, ["all-speeds", "speed", `${type}-speed`], extras, null);
  };

  const prepareType = (type: MovementType): void => {
    const fromSynthetics = (synthetics.movementTypes[type] ?? []).map((entry) => entry.deferred({ test: baseOptions })).filter((s): s is BaseSpeedSynthetic => s !== null);
    const candidates: Candidate[] = fromSynthetics.map((s) => ({ value: s.value, source: s.source, force: s.force, formula: s.formula, dependsOn: s.dependsOn }));
    const fromSystem = systemValues[type];
    if (fromSystem) candidates.push({ value: fromSystem.value, source: fromSystem.source, force: false, formula: fromSystem.value, dependsOn: [] });
    const selected = selectCandidate(candidates);
    if (type !== "land" && !selected) {
      speeds[type] = null;
      return;
    }
    const chosen = selected ?? { value: 0, source: null, force: false, formula: 0, dependsOn: [] };
    if (chosen.value > 0) state.rollOptions.set("all", `speed:${type}`);
    const statistic = build(type, chosen);
    statistics[type] = statistic;
    const trace: SpeedData = { type, value: statistic.value, base: statistic.base, source: statistic.source, label: format(state, "TypeLabel", { type: typeLabel(state, type) }) };
    if (type === "land") speeds.land = trace;
    else speeds[type] = trace;
  };

  // Types in dependency order (a fly speed reading the land total waits for land), then any cycle.
  const pending = new Set<MovementType>(MOVEMENT_TYPES);
  for (let round = 0; round < MOVEMENT_TYPES.length; round++) {
    const ready = MOVEMENT_TYPES.filter(
      (type) =>
        pending.has(type) &&
        (synthetics.movementTypes[type] ?? []).filter((entry) => entry.test({ test: baseOptions })).every((entry) => entry.dependsOn.every((dep) => !pending.has(dep))),
    );
    if (ready.length === 0) break;
    for (const type of ready) {
      prepareType(type);
      pending.delete(type);
    }
  }
  for (const type of pending) prepareType(type);

  const out: Partial<Record<MovementType, DerivedSpeed>> = {};
  for (const type of MOVEMENT_TYPES) {
    const statistic = statistics[type];
    if (!statistic) continue;
    const { breakdown, displayBase } = breakdownOf(state, statistic);
    out[type] = {
      type,
      label: format(state, "TypeLabel", { type: typeLabel(state, type) }),
      base: statistic.base,
      source: statistic.source,
      displayBase,
      value: statistic.value,
      modifiers: statistic.modifiers,
      breakdown,
      derivedFrom: statistic.parent?.type ?? null,
      domains: statistic.domains,
    };
    if (speeds[type]) speeds[type]!.breakdown = breakdown;
  }
  const land = out.land!;
  actor.system.attributes.speed.value = land.value;
  return { ...out, land };
}
